// Timeline Context Menu Service - Core business logic

import { deleteClipInstance } from '@/features/clips/clipsThunks';
import type {
  PersistentClickPosition,
  ClipProvider,
  AssetProvider,
  TimelineStateProvider,
  TimelineCallbackProvider,
  SceneProvider
} from './types';

export class TimelineContextMenuService {
  private dispatch: any;
  private services: any;

  constructor(
    private readonly clipProvider: ClipProvider,
    private readonly assetProvider: AssetProvider,
    private readonly timelineStateProvider: TimelineStateProvider,
    private readonly timelineCallbackProvider: TimelineCallbackProvider,
    private readonly sceneProvider: SceneProvider
  ) { }

  // Add this method to TimelineContextMenuService class
  public injectProviders(
    stateProvider: TimelineStateProvider,
    callbackProvider: TimelineCallbackProvider,
    assetProvider: AssetProvider,
    sceneProvider: SceneProvider
  ): void {
    // Replace the placeholder providers with actual implementations
    (this as any).timelineStateProvider = stateProvider;
    (this as any).timelineCallbackProvider = callbackProvider;
    (this as any).assetProvider = assetProvider;
    (this as any).sceneProvider = sceneProvider;
  }

  // Inject Redux dispatch and services
  public injectRedux(dispatch: any, services: any): void {
    this.dispatch = dispatch;
    this.services = services;
  }


  async editKeyframe(persistentClickPosition: PersistentClickPosition | null, keyframesEnabled: boolean): Promise<void> {
    if (!keyframesEnabled) return;
    await this.handleEditKeyframe(persistentClickPosition);

    // Switch to keyframes tab for the asset
    if (persistentClickPosition?.assetInstanceId && this.timelineCallbackProvider.onOpenTimelineTab) {
      this.timelineCallbackProvider.onOpenTimelineTab(persistentClickPosition.assetInstanceId);
    }

    // Keep context menu visible in keyframe edit mode - don't hide it
    // The context menu will show the keyframe edit mode pill instead of buttons
  }

  addAnimation(persistentClickPosition: PersistentClickPosition | null): void {
    this.timelineStateProvider.setIsInAddAnimationFlow(true);
    this.handleAddAnimation(persistentClickPosition);
    // Keep context menu open for add-animation
  }

  async editAnimation(persistentClickPosition: PersistentClickPosition | null): Promise<void> {
    if (!persistentClickPosition?.assetInstanceId || persistentClickPosition?.timePosition === undefined) {
      return;
    }

    // Find the clip at this position to get its ID
    let clipId: string | undefined;
    try {
      const asset = await this.assetProvider.findAssetById(persistentClickPosition.assetInstanceId);
      if (asset) {
        const sceneId = this.sceneProvider.getSceneId();
        const allSceneClips = await this.clipProvider.fetchAllSceneClips(sceneId);
        const assetClips = allSceneClips.filter(clip => clip.assetId === persistentClickPosition.assetInstanceId);

        const clickTime = persistentClickPosition.timePosition;

        // Find clip at position
        for (const clip of assetClips) {
          const clipAbsoluteStart = clip.instanceStartTime;
          const clipAbsoluteEnd = clip.instanceStartTime + clip.instanceDuration;

          if (clickTime >= clipAbsoluteStart && clickTime <= clipAbsoluteEnd) {
            clipId = clip.instanceId;
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error finding clip for edit animation:', error);
    }

    // Open animations tab and pass the clipId so it knows which clip to select
    if (persistentClickPosition?.assetInstanceId && this.timelineCallbackProvider.onOpenAssetClipsTab) {
      this.timelineCallbackProvider.onOpenAssetClipsTab(
        persistentClickPosition.assetInstanceId,
        persistentClickPosition.timePosition,
        clipId
      );
    }
  }

  exitKeyframeMode(persistentClickPosition: PersistentClickPosition | null): void {
    this.handleExitKeyframeMode(persistentClickPosition);
    // Don't close context menu when exiting keyframe edit mode - user should stay in context menu
  }

  // Helper methods for TimelineContextMenu component
  enterKeyframeEditMode(persistentClickPosition: PersistentClickPosition | null): void {
    if (!persistentClickPosition?.assetInstanceId) return;
    // Keyframe edit mode removed - this method is now a no-op
  }

  openTimelineTab(persistentClickPosition: PersistentClickPosition | null): void {
    if (persistentClickPosition?.assetInstanceId && this.timelineCallbackProvider.onOpenTimelineTab) {
      this.timelineCallbackProvider.onOpenTimelineTab(persistentClickPosition.assetInstanceId);
    }
  }

  selectKeyframe(keyframeId: string | null, assetInstanceId?: string): void {
    if (this.timelineCallbackProvider.onKeyframeSelect) {
      this.timelineCallbackProvider.onKeyframeSelect(keyframeId, assetInstanceId);
    }
  }

  async deleteClip(clipId: string, assetId?: string): Promise<void> {
    console.log('🚀 deleteClip called with clipId:', clipId, 'assetId:', assetId);

    try {
      const sceneId = this.sceneProvider.getSceneId();
      const success = await this.dispatch(deleteClipInstance(clipId, assetId || '', sceneId, this.services));
      
      if (success) {
        // Update context menu state
        this.timelineStateProvider.setContextMenuState({
          hasClip: false,
          isFadeClip: false,
          clipId: undefined
        });

        // Clear clip selection to trigger properties panel update
        this.timelineStateProvider.setSelectedClipId(null);
      }
    } catch (error) {
      console.error('💥 Error in deleteClip:', error);
    }
  }


  private async handleEditKeyframe(persistentClickPosition: PersistentClickPosition | null): Promise<void> {
    if (!persistentClickPosition?.assetInstanceId || persistentClickPosition?.timePosition === undefined) {
      return;
    }

    // Delegate to KeyframeManager via onKeyframeSelect callback
    // The KeyframeManager has access to centralized store keyframes
    if (this.timelineCallbackProvider.onKeyframeSelect) {
      // Pass null to let the KeyframeManager find and select the closest keyframe
      // based on the click position (it will be handled in KeyframeManager's handleKeyframeSelect)
      this.timelineCallbackProvider.onKeyframeSelect(null, persistentClickPosition.assetInstanceId);
    }
  }

  private async handleAddAnimation(persistentClickPosition: PersistentClickPosition | null): Promise<void> {
    if (!persistentClickPosition?.assetInstanceId || !this.timelineCallbackProvider.onOpenAssetClipsTab) {
      return;
    }

    this.timelineCallbackProvider.onOpenAssetClipsTab(
      persistentClickPosition.assetInstanceId,
      persistentClickPosition.timePosition || undefined
    );
  }


  private async handleExitKeyframeMode(persistentClickPosition: PersistentClickPosition | null): Promise<void> {
    console.log('🎯 handleExitKeyframeMode called with:', persistentClickPosition);

    // Open keyframes tab in properties panel if we have a persistent click position
    if (persistentClickPosition?.assetInstanceId && this.timelineCallbackProvider.onOpenTimelineTab) {
      console.log('🎯 Opening keyframes tab for asset:', persistentClickPosition.assetInstanceId);
      this.timelineCallbackProvider.onOpenTimelineTab(persistentClickPosition.assetInstanceId);
    } else {
      console.log('❌ Cannot open keyframes tab - missing assetInstanceId or callback:', {
        assetInstanceId: persistentClickPosition?.assetInstanceId,
        hasCallback: !!this.timelineCallbackProvider.onOpenTimelineTab
      });
    }
  }

  private async handleDeleteClip(persistentClickPosition: PersistentClickPosition | null): Promise<void> {
    console.log('🔍 handleDeleteClip called with:', {
      persistentClickPosition,
      hasAssetId: !!persistentClickPosition?.assetInstanceId,
      hasTimePosition: persistentClickPosition?.timePosition !== undefined
    });

    if (!persistentClickPosition?.assetInstanceId || persistentClickPosition?.timePosition === undefined) {
      console.log('❌ Early return: missing assetInstanceId or timePosition');
      return;
    }

    try {
      console.log('🔍 Finding asset...');
      // Find the asset and its clips
      const asset = await this.assetProvider.findAssetById(persistentClickPosition.assetInstanceId);
      if (!asset) {
        console.error('Asset not found for clip deletion');
        return;
      }
      console.log('✅ Asset found:', asset);

      const clickTime = persistentClickPosition.timePosition;

      console.log('🔍 Getting scene clips...');
      // Find clips associated with this asset
      const sceneId = this.sceneProvider.getSceneId();
      const allSceneClips = await this.clipProvider.fetchAllSceneClips(sceneId);
      console.log('✅ All scene clips:', allSceneClips.length);
      const assetClips = allSceneClips.filter(clip => clip.assetId === persistentClickPosition.assetInstanceId);
      console.log('✅ Asset clips found:', assetClips.length, assetClips);

      if (assetClips && assetClips.length > 0) {
        // Find the clip at the click position
        let clipToDelete = null;
        console.log('🔍 Looking for clip at clickTime:', clickTime);

        for (const clip of assetClips) {
          const clipStartTime = clip.instanceStartTime || 0;
          const clipEndTime = (clip.instanceStartTime || 0) + (clip.instanceDuration || 0);

          console.log('🎯 Checking clip:', {
            clipId: clip.instanceId,
            clipStartTime,
            clipEndTime,
            clipDuration: clip.instanceDuration,
            isMatch: clickTime >= clipStartTime && clickTime <= clipEndTime
          });

          if (clickTime >= clipStartTime && clickTime <= clipEndTime) {
            clipToDelete = clip;
            break;
          }
        }

        console.log('🎯 Clip to delete:', clipToDelete?.instanceId || 'NONE FOUND');

        if (clipToDelete) {
          console.log('🗑️ Deleting clip at click position:', {
            clipId: clipToDelete.instanceId,
            clipStartTime: clipToDelete.instanceStartTime,
            clickTime: clickTime
          });

          // Delete the clip
          await this.clipProvider.deleteClipInstance(clipToDelete.instanceId);

          // Trigger fast clips refresh to update UI (pass deleted clip info for targeted update)
          console.log('🔄 Calling onClipsRefresh with:', {
            deletedClipId: clipToDelete.instanceId,
            assetInstanceId: persistentClickPosition.assetInstanceId,
            hasRefreshCallback: !!this.timelineCallbackProvider.onClipsRefresh
          });

          this.timelineCallbackProvider.onClipsRefresh?.(clipToDelete.instanceId, persistentClickPosition.assetInstanceId);

          console.log('✅ Clip deleted successfully and UI refresh triggered');
        }
      }
    } catch (error) {
      console.error('Error deleting clip:', error);
    }
  }

  // Context menu activation - replaces Timeline component logic
  handleContextMenuClick(clickData: {
    hasKeyframe: boolean;
    hasClip: boolean;
    hasTrigger?: boolean;
    isKeyframeEditMode: boolean;
    clickPosition?: { x: number; y: number };
    assetInstanceId?: string;
    clickTimePosition?: number;
    assetStartTime?: number;
    keyframeCategory?: string;
    isFadeClip?: boolean;
    keyframesEnabled?: boolean;
    clipId?: string;
    keyframeId?: string;
    triggerId?: string;
  }, timelineContainerRef: React.RefObject<HTMLElement>, sceneAssetsArray: any[]): boolean {
    // Determine if the asset supports keyframes and clips
    let supportsKeyframesAndClips = true;
    if (clickData.assetInstanceId) {
      const asset = sceneAssetsArray.find(a => a.instanceId === clickData.assetInstanceId);
      if (asset) {
        // Camera, Audio, and Image Anchor assets cannot have keyframes or clips
        // TODO add immersal back in and image anchor
        supportsKeyframesAndClips = asset.instanceCategory !== 'camera' && asset.instanceCategory !== 'audio';
      }
    }

    // Don't show context menu for assets that don't support keyframes and clips
    if (!supportsKeyframesAndClips) {
      return false;
    }

    // Calculate persistent click position
    if (clickData.clickPosition && clickData.assetInstanceId && clickData.clickTimePosition !== undefined && clickData.assetStartTime !== undefined) {
      this.calculateAndSetPersistentPosition(clickData, timelineContainerRef);
    }

    // Set context menu state
    this.timelineStateProvider.setContextMenuState({
      isVisible: true,
      supportsKeyframesAndClips,
      keyframesEnabled: clickData.keyframesEnabled ?? true,
      ...clickData,
    });

    // Update properties panel with context menu data
    if (clickData.assetInstanceId && clickData.clickTimePosition !== undefined && this.timelineCallbackProvider.onPropertiesPanelUpdate) {
      this.timelineCallbackProvider.onPropertiesPanelUpdate(
        clickData.assetInstanceId,
        clickData.clickTimePosition
      );
    }

    return true;
  }

  private calculateAndSetPersistentPosition(clickData: any, timelineContainerRef: React.RefObject<HTMLElement>) {
    if (!timelineContainerRef.current) return;

    // Find the specific asset element to calculate asset-relative X
    const assetElements = document.querySelectorAll('[data-timeline-category-item-row]');
    let assetElement = null;

    // Find the correct asset row by checking if it contains an element with the asset ID
    for (const element of assetElements) {
      if (element.querySelector(`[data-asset-id="${clickData.assetInstanceId}"]`)) {
        assetElement = element;
        break;
      }
    }

    let assetRelativeX = 0; // fallback

    if (assetElement) {
      const assetRect = assetElement.getBoundingClientRect();
      // Calculate asset-relative X coordinate
      assetRelativeX = clickData.clickPosition.x - assetRect.left;
    }

    this.timelineStateProvider.setPersistentClickPosition({
      x: 0, // Not used anymore
      y: 0, // Not used anymore
      assetRelativeX: assetRelativeX,
      timePosition: clickData.clickTimePosition,
      assetInstanceId: clickData.assetInstanceId,
      assetStartTime: clickData.assetStartTime,
      assetEndTime: clickData.assetEndTime || clickData.assetStartTime
    });
  }
}
